import { useRef, useEffect, useCallback } from 'react';
import Shepherd from 'shepherd.js';
import 'shepherd.js/dist/css/shepherd.css';
import { offset } from '@floating-ui/dom';
import { TOUR_STORAGE_KEY } from '../utils/costingHelpers';

const waitForRender = (ms = 400) => new Promise(resolve => setTimeout(resolve, ms));

const useCostingSummaryTour = ({
    dataReady,
    preloadedData,
    setFilters,
    handleSummaryRowSelect,
    handleChartBarClick,
    handleClearAll,
    setLeftSelection,
    setRightSelection,
    setActiveTable,
    setChartSelection
}) => {
    const tourRef = useRef(null);
    const tourActiveRef = useRef(false);
    const tourCompletedByUser = useRef(false);

    const getFirstWeek = useCallback(() => {
        if (!preloadedData || preloadedData.length === 0) return null;
        return preloadedData[0]?.weekEnding || null;
    }, [preloadedData]);

    const getFirstRole = useCallback(() => {
        if (!preloadedData || preloadedData.length === 0) return null;
        return preloadedData[0]?.roleName || null;
    }, [preloadedData]);

    const createTour = useCallback(() => {
        if (tourRef.current) {
            tourRef.current.off('complete');
            tourRef.current.off('cancel');
            tourRef.current.complete();
        }

        const tour = new Shepherd.Tour({
            useModalOverlay: true,
            defaultStepOptions: {
                cancelIcon: { enabled: true },
                scrollTo: { behavior: 'smooth', block: 'center' },
                classes: 'shepherd-theme-custom'
            }
        });

        const steps = [
            {
                id: 'filters',
                attachTo: { element: '.filter-row .filter-item:nth-child(1)', on: 'bottom' },
                title: 'Filters',
                text: 'Use these filters to narrow your data by site, provider, role, week, or period.',
                buttons: [
                    { text: 'Skip', action: tour.complete, secondary: true },
                    { text: 'Next', action: tour.next }
                ]
            },
            {
                id: 'expand-week',
                attachTo: { element: '.hierarchical-summary-table .period-row td:first-child', on: 'right' },
                floatingUIOptions: {
                    middleware: [offset(16)]
                },
                title: 'Expand & Drill Down',
                text: 'Click the arrows to expand weeks into sites and roles. Click any row to see its details on the right.',
                beforeShowPromise: () => {
                    const firstWeek = getFirstWeek();
                    if (firstWeek) {
                        handleSummaryRowSelect({
                            type: 'week',
                            weekKey: new Date(firstWeek).toISOString(),
                            weekEnding: firstWeek
                        });
                    }
                    return waitForRender(600);
                },
                buttons: [
                    { text: 'Back', action: tour.back, secondary: true },
                    { text: 'Next', action: tour.next }
                ]
            },
            {
                id: 'detail-table',
                attachTo: { element: '.right-table-container', on: 'left' },
                title: 'Detail Table',
                text: 'The detailed breakdown of your selection appears here. Click any row to filter the summary table back.',
                buttons: [
                    { text: 'Back', action: tour.back, secondary: true },
                    { text: 'Next', action: tour.next }
                ]
            },
            {
                id: 'day-columns',
                attachTo: { element: '.hierarchical-summary-table thead', on: 'bottom' },
                title: 'Day Columns',
                text: 'Click any day column header to filter all data to just that day.',
                buttons: [
                    { text: 'Back', action: tour.back, secondary: true },
                    { text: 'Next', action: tour.next }
                ]
            },
            {
                id: 'chart-bar-click',
                attachTo: { element: '.chart-container', on: 'top' },
                title: 'Cost Chart',
                text: 'Costs are broken down by role per week. Click a bar to filter both tables to that role and week.',
                beforeShowPromise: () => {
                    setLeftSelection(null);
                    setRightSelection(null);
                    setActiveTable(null);
                    setChartSelection(null);
                    const firstWeek = getFirstWeek();
                    const firstRole = getFirstRole();
                    if (firstWeek && firstRole) {
                        handleChartBarClick({
                            group: new Date(firstWeek).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' }),
                            role: firstRole,
                            weekEnding: firstWeek
                        });
                    }
                    return waitForRender(600);
                },
                buttons: [
                    { text: 'Back', action: tour.back, secondary: true },
                    { text: 'Done', action: tour.complete }
                ]
            }
        ];

        steps.forEach(step => tour.addStep(step));

        const cleanupAfterTour = () => {
            tourActiveRef.current = false;
            handleClearAll();
            if (tourCompletedByUser.current) {
                localStorage.setItem(TOUR_STORAGE_KEY, 'true');
            }
        };

        tour.on('complete', cleanupAfterTour);
        tour.on('cancel', () => {
            tourActiveRef.current = false;
            handleClearAll();
            localStorage.setItem(TOUR_STORAGE_KEY, 'true');
        });

        tourRef.current = tour;
        return tour;
    }, [getFirstWeek, getFirstRole, handleSummaryRowSelect, handleChartBarClick, handleClearAll, setFilters, setLeftSelection, setRightSelection, setActiveTable, setChartSelection]);

    const startTour = useCallback(() => {
        if (tourActiveRef.current) return;
        tourActiveRef.current = true;
        tourCompletedByUser.current = true;
        const tour = createTour();
        tour.start();
    }, [createTour]);

    useEffect(() => {
        if (!dataReady || localStorage.getItem(TOUR_STORAGE_KEY)) return;

        const interval = setInterval(() => {
            const table = document.querySelector('.hierarchical-summary-table');
            const chart = document.querySelector('.role-cost-chart');
            if (table && chart) {
                clearInterval(interval);
                startTour();
            }
        }, 300);

        return () => clearInterval(interval);
    }, [dataReady, startTour]);

    useEffect(() => {
        return () => {
            if (tourRef.current) {
                tourRef.current.off('complete');
                tourRef.current.off('cancel');
                tourRef.current.complete();
            }
            tourActiveRef.current = false;
        };
    }, []);

    return { startTour };
};

export default useCostingSummaryTour;
